/* One device, one panel — wherever it was found.
 *
 * This lived inside the devices screen, which was fine while there was one list
 * and became the reason clicking a printer threw you onto a different tab. Three
 * lists reach the same record, so the record cannot belong to one of them.
 *
 * Self-contained on purpose. It takes a device and fetches what it needs to
 * describe it, rather than being handed six things by every screen that shows
 * it — the queries are shared by key, so a screen that already loaded people is
 * not loading them twice.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useOnline } from '../../hooks/useOnline';
import { recordHref } from '../../hooks/useOpenRecord';
import { countdown, daysSince, expiryTone, fmtDate, yearsOld } from '../../lib/format';
import { groupOf, listFor, listName, traitsFor } from '../../lib/deviceGroups';
import { subnetFor } from '../../lib/network';
import { deviceSources, renamedTo } from '../../lib/registers';
import { historyDisagrees } from '../../lib/lifecycle';
import { depotName } from '../../services/depots';
import type { IDevice } from '../../models';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import {
  Badge,
  Button,
  Drawer,
  KeyValue,
  RelationCard,
  SummaryTile,
  SummaryTiles,
} from '../ui';
import { Copyable } from './Copyable';
import { Explain } from './Explain';
import s from './screens.module.scss';
import { PRODUCT } from '../../lib/product';

const DEVICE_ICON: Record<string, string> = {
  Laptop: 'ti-device-laptop',
  Desktop: 'ti-device-desktop',
  Mobile: 'ti-device-mobile',
  Tablet: 'ti-device-ipad',
  Server: 'ti-server-2',
  Firewall: 'ti-shield-lock',
  Switch: 'ti-topology-star-3',
  'Access point': 'ti-access-point',
  Printer: 'ti-printer',
  Storage: 'ti-database',
  UPS: 'ti-battery-charging',
};

function complianceTone(value: string) {
  if (/^compliant/i.test(value)) return 'ok' as const;
  if (/non/i.test(value)) return 'bad' as const;
  return value ? 'warn' : ('muted' as const);
}

export function DeviceDetail({
  device,
  onClose,
  onEdit,
}: {
  device: IDevice | null;
  onClose: () => void;
  /** Omitted where a screen does not offer editing from the panel. */
  onEdit?: (device: IDevice) => void;
}) {
  const svc = useServices();
  const online = useOnline();
  const qc = useQueryClient();

  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list() });
  const assignments = useQuery({
    queryKey: qk.assignments,
    queryFn: () => svc.devices.assignments(),
  });
  const devices = useQuery({ queryKey: qk.devices, queryFn: () => svc.devices.list() });
  const networks = useQuery({ queryKey: qk.networks, queryFn: () => svc.networks.all() });
  const attachments = useQuery({
    queryKey: qk.attachments(device?.id ?? ''),
    queryFn: () => svc.attachments.forRecord(device!.id),
    enabled: device !== null,
  });

  const [notes, setNotes] = useState('');

  /* Reset when the record changes, not on mount — the panel stays mounted
     between devices, so without this the second one you open shows the first
     one's notes. */
  useEffect(() => {
    setNotes(device?.notes ?? '');
  }, [device]);

  const saveNotes = useMutation({

    meta: { ok: 'Notes saved' },
    mutationFn: ({ id, value }: { id: string; value: string }) =>
      svc.devices.update(id, { notes: value }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.devices });
      void qc.invalidateQueries({ queryKey: qk.audit });
    },
  });

  const personName = (id: string) =>
    (people.data ?? []).find((p) => p.id === id)?.name ?? 'Unknown';

  /* What this kind of thing actually has. The form has known since the printer
     fields went in; the panel did not, which is why a printer's record showed
     Specification, Operating system and Encryption — three questions no printer
     can answer, on the panel somebody opens to find a cartridge code.

     Reporting them as "Unknown" was the worst of both: it reads as missing data
     somebody ought to go and fill in. */
  const has = traitsFor(device?.type ?? '');
  const hired = device?.ownership === 'Hired';

  return (
    <Drawer
      open={device !== null}
      title={device ? (renamedTo(device) ?? device.tag) : ''}
      sub={device ? `${renamedTo(device) ? `Asset ${device.tag} · ` : ''}${device.make} ${device.model}` : undefined}
      avatar={device && <i className={`ti ${DEVICE_ICON[device.type] ?? 'ti-device-desktop'}`} aria-hidden />}
      meta={
        device && (
          <>
            {has.managedState && device.compliance && (
              <Badge tone={complianceTone(device.compliance)}>{device.compliance}</Badge>
            )}
            {hired ? (
              device.hireEnds && (
                <Badge tone={expiryTone(device.hireEnds)}>Hire {countdown(device.hireEnds)}</Badge>
              )
            ) : (
              device.warranty && (
                <Badge tone={expiryTone(device.warranty)}>Warranty {countdown(device.warranty)}</Badge>
              )
            )}
            <Badge tone="muted">{device.src}</Badge>
          </>
        )
      }
      /* Pinned, not at the end of the body: see PeopleScreen. */
      footer={device ? (
        <div className={s.actions}>
          <Button
            primary
            icon="ti-device-floppy"
            disabled={!online || saveNotes.isPending || notes === device.notes}
            onClick={() => saveNotes.mutate({ id: device.id, value: notes })}
          >
            {saveNotes.isPending ? 'Saving…' : 'Save notes'}
          </Button>
          <Button icon="ti-pencil" disabled={!online} onClick={() => onEdit?.(device)}>
            Edit details
          </Button>
          {!online && <div className={s.hint}>Editing is disabled while offline.</div>}
        </div>
      ) : undefined}
      onClose={onClose}
    >
      {device && (
        <>
          {/* Reached from another list. The record is the same wherever it is
              opened from — one machine, one panel — but the table behind this
              one does not contain it, and leaving somebody to notice that for
              themselves is how a split register starts feeling broken. */}
          {/* Two lists hold the same fact — the pointer on this device and the
              open row in the assignment history. assign() writes them in an
              order that makes a half-failure leave the pointer right, and
              reports what it could not do, but that report is read once by
              whoever pressed the button. This is the part that stays true,
              and it is here rather than in a log because a disagreement
              nobody can see gets believed by whichever screen is asked
              first. */}
          {(() => {
            const wrong = historyDisagrees(device, assignments.data ?? []);
            return wrong ? (
              <div className={s.ownedNote}>
                <i className="ti ti-alert-triangle" aria-hidden />
                <span>
                  <b>The assignment history does not match.</b> {wrong} Re-assigning the device
                  from the Onboarding screen will put both back in step.
                </span>
              </div>
            ) : null;
          })()}

          {groupOf(device.type) !== 'endpoint' && (
            <div className={s.ownedNote}>
              <i className="ti ti-info-circle" aria-hidden />
              <span>
                A {device.type.toLowerCase()} is listed under{' '}
                <Link to={listFor(device.type)} className={s.inlineLink}>
                  {listName(device.type)}
                </Link>
                , not here. This is the same record either way.
              </span>
            </div>
          )}

          {/* What somebody opens a device to find out, before reading a spec
              sheet: is it healthy, is it protected, is it still covered, and
              has it phoned home lately. */}
          <SummaryTiles>
            {/* Compliance and encryption are Intune's, and Intune manages
                endpoints and servers. A UPS reporting "Unknown" for both is not
                missing data, it is two questions that were never going to have
                an answer. */}
            {has.managedState && (
              <SummaryTile
                icon="ti-shield-check"
                value={device.compliance || 'Unknown'}
                label="Compliance"
                tone={/compliant/i.test(device.compliance) && !/non/i.test(device.compliance) ? 'ok' : device.compliance ? 'warn' : 'muted'}
              />
            )}
            {has.managedState && (
              <SummaryTile
                icon="ti-lock"
                value={device.enc || 'Unknown'}
                label="Encryption"
                tone={device.enc ? (/not|off|none/i.test(device.enc) ? 'bad' : 'ok') : 'muted'}
              />
            )}
            {/* A hired machine has no warranty — the contract is the warranty —
                so it gets the date that actually runs out. */}
            {hired ? (
              <SummaryTile
                icon="ti-file-description"
                value={device.hireEnds ? countdown(device.hireEnds) : 'Not recorded'}
                label="Hire ends"
                tone={device.hireEnds ? expiryTone(device.hireEnds) : 'warn'}
              />
            ) : (
              <SummaryTile
                icon="ti-shield-dollar"
                value={device.warranty ? countdown(device.warranty) : 'Not recorded'}
                label="Warranty"
                tone={device.warranty ? expiryTone(device.warranty) : 'warn'}
              />
            )}
            <SummaryTile
              icon="ti-cloud-check"
              value={device.seen ? `${daysSince(device.seen)}d` : 'Never'}
              label="Last seen"
              tone={!device.seen ? 'muted' : daysSince(device.seen) > 30 ? 'warn' : 'ok'}
            />
          </SummaryTiles>

          {/* Sectioned, and the sections are the point rather than tidiness.
              A flat list of twelve rows made an iLO address the eleventh
              thing on a panel, which is the same as hiding it — and it is
              the one field here somebody needs while everything else on the
              machine has stopped answering. */}
          <KeyValue
            rows={[
              { section: 'Identity', key: 'Serial', value: <span className={s.mono}>{device.serial}</span> },
              { section: 'Identity', key: 'Type', value: device.type },
              /* Infrastructure belongs to the building, so there is nobody to
                 name — and a row reading "Nobody" against a firewall looks like
                 an omission rather than the answer. */
              ...(has.assignable
                ? [
                    {
                      section: 'Identity',
                      key: 'Assigned to',
                      value: device.user ? (
                        <Link to={recordHref('/people', device.user)} className={s.inlineLink}>
                          {personName(device.user)}
                        </Link>
                      ) : (
                        'Nobody'
                      ),
                    },
                  ]
                : []),
              { section: 'Identity', key: 'Depot', value: depotName(device.loc), owned: true },
              { section: 'Identity', key: 'Status', value: device.status, owned: true },

              /* Reaching it. The OS address and, on a server, the controller
                 that answers when the OS does not. */
              ...(device.ip
                ? [{ section: 'Reaching it', key: 'IP address', value: <Copyable value={device.ip} /> }]
                : []),
              ...(device.mgmtAddress
                ? [
                    {
                      section: 'Reaching it',
                      key: device.mgmtType ?? 'Management',
                      value: <Copyable value={device.mgmtAddress} />,
                      owned: true,
                    },
                  ]
                : []),
              ...(device.mgmtCredential
                ? [
                    {
                      section: 'Reaching it',
                      key: 'Login held in',
                      /* The name of a credentials entry, never the password —
                         §16, and a controller that can power the machine off
                         is the last place a stored one would be harmless. */
                      value: (
                        <Link to={recordHref('/credentials', device.mgmtCredential)} className={s.inlineLink}>
                          {device.mgmtCredential}
                        </Link>
                      ),
                      owned: true,
                    },
                  ]
                : []),

              ...(device.type === 'Printer'
                ? [
                    { section: 'Printer', key: 'Cartridges', value: <span className={s.mono}>{device.consumables ?? ''}</span>, owned: true },
                    { section: 'Printer', key: 'Where it is', value: device.placement ?? '', owned: true },
                    { section: 'Printer', key: 'Print queue', value: device.printQueue ?? '', owned: true },
                    {
                      section: 'Printer',
                      key: 'Meter',
                      value: device.meterReading
                        ? `${device.meterReading.toLocaleString()}${device.meterDate ? ` on ${fmtDate(device.meterDate)}` : ''}`
                        : '',
                      owned: true,
                    },
                  ]
                : []),

                ...(has.spec
                  ? [
                    { section: 'The machine', key: 'Specification', value: [device.cpu, device.ram, device.disk].filter((v) => v && v !== '—').join(' · ') },
                    { section: 'The machine', key: 'Operating system', value: device.os },
                    ]
                  : []),
                ...(has.managedState
                  ? [{ section: 'The machine', key: 'Encryption', value: device.enc }]
                  : []),

                /* Owned or hired, never both — the same rule the expiry
                   aggregation follows, so the panel and the Expiries screen
                   cannot disagree about which date a machine has. */
                ...(hired
                  ? [
                    { section: 'The machine', key: 'Hired from', value: device.hireSupplier ?? '', owned: true },
                    { section: 'The machine', key: 'Contract', value: device.hireRef ?? '', owned: true },
                    { section: 'The machine', key: 'Hire ends', value: device.hireEnds ? fmtDate(device.hireEnds) : '', owned: true },
                    { section: 'The machine', key: 'Cost a month', value: device.hireCost ? `£${device.hireCost}` : '', owned: true },
                    ]
                  : [
                    { section: 'The machine', key: 'Purchased', value: device.bought ? `${fmtDate(device.bought)} (${yearsOld(device.bought)} yr)` : '', owned: true },
                    { section: 'The machine', key: 'Warranty ends', value: fmtDate(device.warranty), owned: true },
                    ]),

              { section: 'The machine', key: 'Last seen', value: `${fmtDate(device.seen)} (${countdown(device.seen)})` },
            ]}
          />

          <Explain title="Which of these fields are yours" icon="ti-shield-check">
            {/* Derived from the ids the syncs write, not from RecordSource.
                One hand-typed choice cannot describe a laptop that NinjaOne
                and Intune both touch, and left stale it says "these come from
                Manual and are refreshed on the nightly sync". */}
            {(() => {
              const sources = deviceSources(device);
              if (sources.length === 0) {
                return (
                  <>
                    Nothing syncs this record — every field on it is {PRODUCT.name}'s, and none of it
                    is overwritten. Kit that runs no agent, like a firewall or a printer, is only
                    ever as good as what somebody typed.
                  </>
                );
              }
              return (
                <>
                  The fields with a yellow edge are {PRODUCT.name}'s own and nothing overwrites them.{' '}
                  {sources.map((source, i) => (
                    <span key={source.name}>
                      {i > 0 && ' '}
                      <b>{source.name}</b> refreshes {source.owns}
                      {i === sources.length - 1 ? '' : ';'}
                    </span>
                  ))}
                  , on the nightly sync.
                </>
              );
            })()}
          </Explain>

          {device && (() => {
            const holder = (people.data ?? []).find((p) => p.id === device.user);
            const subnet = subnetFor(
              device.ip ?? '',
              networks.data?.[device.loc]?.subnets ?? [],
            );
            const siblings = holder
              ? (devices.data ?? []).filter((d) => d.user === holder.id && d.id !== device.id)
              : [];

            return (
              <>
                {/* The joins already existed in the data and nowhere in the
                    screen. Related items is the feature that turns a set of
                    registers into documentation — it is what IT Glue and Hudu
                    are really selling. */}
                <RelationCard
                  icon="ti-user"
                  title="Signed out to"
                  count={holder ? 1 : 0}
                  preview={holder?.name}
                  defaultOpen
                >
                  {holder && (
                    <a className={`${s.row} ${s.rowFlush}`} href={recordHref('/people', holder.upn)}>
                      <div className={s.rowIcon}>
                        <i className="ti ti-user" aria-hidden />
                      </div>
                      <div className={s.rowMain}>
                        <div className={s.rowTitle}>{holder.name}</div>
                        <div className={s.rowSub}>
                          {holder.title || 'No job title'} · {holder.status}
                        </div>
                      </div>
                    </a>
                  )}
                </RelationCard>

                <RelationCard
                  icon="ti-router"
                  title="Subnet"
                  count={subnet ? 1 : 0}
                  preview={subnet && `VLAN ${subnet.vlan} · ${subnet.name}`}
                >
                  {subnet && (
                    <a className={`${s.row} ${s.rowFlush}`} href="/network">
                      <div className={s.rowIcon}>
                        <i className="ti ti-router" aria-hidden />
                      </div>
                      <div className={s.rowMain}>
                        <div className={s.rowTitle}>
                          VLAN {subnet.vlan} · {subnet.name}
                        </div>
                        <div className={s.rowSub}>
                          {subnet.cidr} · gateway {subnet.gw || 'not recorded'}
                        </div>
                      </div>
                    </a>
                  )}
                </RelationCard>

                {/* "What else has this person got" is the question every
                    starter, leaver and lost-laptop conversation starts with. */}
                <RelationCard
                  icon="ti-devices"
                  title={holder ? `Also with ${holder.name.split(' ')[0]}` : 'Also with this person'}
                  count={siblings.length}
                  preview={siblings.slice(0, 3).map((d) => d.tag).join(' · ')}
                >
                  {siblings.map((d) => (
                    <a key={d.id} className={`${s.row} ${s.rowFlush}`} href={recordHref('/devices', d.tag)}>
                      <div className={s.rowIcon}>
                        <i className="ti ti-device-laptop" aria-hidden />
                      </div>
                      <div className={s.rowMain}>
                        <div className={s.rowTitle}>{d.tag}</div>
                        <div className={s.rowSub}>
                          {d.make} {d.model} · {d.type}
                        </div>
                      </div>
                    </a>
                  ))}
                </RelationCard>

                {/* The question a damaged or missing laptop starts with, and
                    the one the pointer above cannot answer: a leaver gives
                    their kit up, so without this the record of who had it goes
                    at exactly the moment somebody asks. */}
                {(() => {
                  const spells = (assignments.data ?? []).filter((a) => a.device === device.id);
                  const nameOf = (id: string): string =>
                    (people.data ?? []).find((p) => p.id === id)?.name ?? 'somebody no longer on the register';
                  if (spells.length === 0) return null;
                  return (
                    <RelationCard
                      icon="ti-history"
                      title="Who has had this"
                      count={spells.length}
                      preview={spells.slice(0, 2).map((a) => nameOf(a.person)).join(' · ')}
                    >
                      {spells.map((a) => (
                        <div key={a.id} className={`${s.row} ${s.rowFlush}`}>
                          <div className={s.rowIcon}>
                            <i className={`ti ${a.to ? 'ti-user-off' : 'ti-user-check'}`} aria-hidden />
                          </div>
                          <div className={s.rowMain}>
                            <div className={s.rowTitle}>{nameOf(a.person)}</div>
                            <div className={s.rowSub}>
                              {fmtDate(a.from)} — {a.to ? fmtDate(a.to) : 'still has it'}
                              {a.to ? ` · ${a.reason}` : ''}
                              {a.notes ? ` · ${a.notes}` : ''}
                            </div>
                          </div>
                        </div>
                      ))}
                    </RelationCard>
                  );
                })()}
              </>
            );
          })()}

          {(attachments.data ?? []).length > 0 && (
            <div>
              <div className={s.sectionLabel}>Attachments</div>
              <div className={s.rows}>
                {(attachments.data ?? []).map((f) => (
                  <div key={f.name} className={`${s.row} ${s.rowFlush}`}>
                    <div className={s.rowIcon}>
                      <i className="ti ti-file" aria-hidden />
                    </div>
                    <div className={s.rowMain}>
                      <div className={s.rowTitle}>{f.name}</div>
                      <div className={s.rowSub}>
                        {f.size} · {f.by} · {fmtDate(f.when)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={s.field}>
            <label htmlFor="device-notes">Notes</label>
            <textarea
              id="device-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything the fields above do not cover."
            />
          </div>
        </>
      )}
    </Drawer>
  );
}
